import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNotifications } from '../hooks/useNotifications';
import MissingItemsList from '../components/customerPortal/MissingItemsList';
import AvailableForms from '../components/customerPortal/AvailableForms';
import MySubmissions from '../components/customerPortal/MySubmissions';
import CustomerDocuments from '../components/customerPortal/CustomerDocuments';
import Subscriptions from '../components/customerPortal/Subscriptions';
import ConfirmDeleteModal from '../components/common/ConfirmDeleteModal';
import { missingItemService } from '../services/missingItemService';
import { customerTicketService } from '../services/customerTicketService';
import { MissingItem } from '../types/missingItem';
import { CustomerTicket, TicketAttachment, TICKET_STATUSES, TICKET_PRIORITIES, TICKET_TYPES } from '../types/customerTicket';
import AttachmentUploader from '../components/customerTickets/AttachmentUploader';
import AttachmentList from '../components/customerTickets/AttachmentList';
import {
  UserCircleIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  FolderIcon,
  Bars3Icon,
  XMarkIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ChevronRightIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';
import Modal from '../components/common/Modal';
import { Button } from '../components/common/Button';
import toast from 'react-hot-toast';

const CustomerPortal = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  
  // Notification system - customer userek is kapnak értesítéseket
  useNotifications();
  
  const [activeTab, setActiveTab] = useState<'overview' | 'projects' | 'submissions' | 'missing-items' | 'documents' | 'subscriptions'>('overview');
  const [missingItemsCount, setMissingItemsCount] = useState(0);
  const [missingItems, setMissingItems] = useState<MissingItem[]>([]);
  const [tickets, setTickets] = useState<CustomerTicket[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [showProjectDetailsModal, setShowProjectDetailsModal] = useState(false);
  const [showDeleteAttachmentModal, setShowDeleteAttachmentModal] = useState(false);
  const [showDeleteTicketModal, setShowDeleteTicketModal] = useState(false);
  const [selectedProjectDetail, setSelectedProjectDetail] = useState<CustomerTicket | null>(null);
  const [deletingAttachment, setDeletingAttachment] = useState<{ attachment: TicketAttachment; ticketId: string } | null>(null);
  const [deletingTicket, setDeletingTicket] = useState<CustomerTicket | null>(null);
  const [newProjectForm, setNewProjectForm] = useState({
    type: 'question' as const,
    title: '',
    description: '',
    priority: 'medium' as const
  });
  const [replyMessage, setReplyMessage] = useState('');
  const [newTicketAttachments, setNewTicketAttachments] = useState<TicketAttachment[]>([]);
  const [replyAttachments, setReplyAttachments] = useState<TicketAttachment[]>([]);

  useEffect(() => {
    if (user?.customerId) {
      loadMissingItems();
      loadTickets();
    }
  }, [user]);

  const loadMissingItems = async () => {
    if (!user?.customerId) return;
    try {
      const items = await missingItemService.getPendingMissingItems(user.customerId);
      setMissingItems(items);
      setMissingItemsCount(items.length);
    } catch (error) {
      console.error('Error loading missing items:', error);
    }
  };

  const loadTickets = async () => {
    if (!user?.customerId) return;
    try {
      setProjectsLoading(true);
      const data = await customerTicketService.getProjectsByCustomer(user.customerId);
      setTickets(data);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setProjectsLoading(false);
    }
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.customerId || !user?.id) return;

    if (!newProjectForm.title.trim()) {
      toast.error('Kérlek add meg a címet!');
      return;
    }

    try {
      // Get customer name for denormalization
      const customerName = user.displayName || 'Ügyfél';

      await customerTicketService.createTicket({
        customerId: user.customerId,
        customerName: customerName,
        organizationId: user.organizationId || '',
        type: newProjectForm.type,
        title: newProjectForm.title,
        description: newProjectForm.description,
        priority: newProjectForm.priority,
        status: 'submitted',
        createdBy: user.id,
        attachments: newTicketAttachments
      });

      toast.success('Kérés elküldve!');
      setShowCreateProjectModal(false);
      setNewProjectForm({
        type: 'question',
        title: '',
        description: '',
        priority: 'medium'
      });
      setNewTicketAttachments([]);
      loadTickets();
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Hiba a kérés létrehozása során');
    }
  };

  const handleSendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProjectDetail || !user?.id || !replyMessage.trim()) return;

    try {
      const customerName = user.displayName || 'Ügyfél';

      await customerTicketService.addResponse(selectedProjectDetail.id, {
        message: replyMessage,
        createdBy: user.id,
        createdByName: customerName,
        isStaff: false,
        attachments: replyAttachments
      });

      // Mellékletek hozzáadása a tickethez is
      for (const attachment of replyAttachments) {
        await customerTicketService.addAttachment(selectedProjectDetail.id, attachment);
      }

      toast.success('Válasz elküldve!');
      setReplyMessage('');
      setReplyAttachments([]);
      loadTickets();
      
      // Refresh selected project
      const updatedProject = await customerTicketService.getProjectById(selectedProjectDetail.id);
      if (updatedProject) {
        setSelectedProjectDetail(updatedProject);
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Hiba a válasz küldése során');
    }
  };

  const handleAttachmentAdded = (attachment: TicketAttachment) => {
    setNewTicketAttachments([...newTicketAttachments, attachment]);
  };

  const handleReplyAttachmentAdded = (attachment: TicketAttachment) => {
    setReplyAttachments([...replyAttachments, attachment]);
  };

  const handleTicketDeleteClick = (ticket: CustomerTicket) => {
    setDeletingTicket(ticket);
    setShowDeleteTicketModal(true);
  };

  const handleConfirmTicketDelete = async () => {
    if (!deletingTicket) return;

    try {
      await customerTicketService.deleteProject(deletingTicket.id);
      toast.success('Kérés törölve');
      setShowDeleteTicketModal(false);
      setDeletingTicket(null);
      loadTickets();
    } catch (error) {
      console.error('Error deleting ticket:', error);
      toast.error('Hiba a törlés során');
    }
  };

  const handleAttachmentDeleteClick = (attachment: TicketAttachment, ticketId: string) => {
    setDeletingAttachment({ attachment, ticketId });
    setShowDeleteAttachmentModal(true);
  };

  const handleConfirmAttachmentDelete = async () => {
    if (!deletingAttachment) return;
    
    try {
      await customerTicketService.removeAttachment(deletingAttachment.ticketId, deletingAttachment.attachment.id);
      toast.success('Melléklet törölve');
      
      // Refresh selected project
      if (selectedProjectDetail && selectedProjectDetail.id === deletingAttachment.ticketId) {
        const updatedProject = await customerTicketService.getProjectById(selectedProjectDetail.id);
        if (updatedProject) {
          setSelectedProjectDetail(updatedProject);
        }
      }
      
      setShowDeleteAttachmentModal(false);
      setDeletingAttachment(null);
      loadTickets();
    } catch (error) {
      console.error('Error deleting attachment:', error);
      toast.error('Hiba a melléklet törlése során');
    }
  };

  // Szűrés
  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = 
      ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || ticket.status === filterStatus;
    const matchesType = filterType === 'all' || ticket.type === filterType;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      {/* Main content area with sidebar */}
      <div className="flex w-full overflow-hidden">
        {/* Sidebar - Desktop */}
        <aside className="hidden xl:flex xl:flex-col xl:w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
          {/* Sidebar Header */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <UserCircleIcon className="h-8 w-8 text-primary-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('customerPortal.title')}
                </h2>
              </div>
            </div>
          </div>
          
          <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all relative ${
                activeTab === 'overview'
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {activeTab === 'overview' && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-r-full" />
              )}
              <ClipboardDocumentListIcon className="h-4 w-4" />
              <span>{t('customerPortal.tabs.overview')}</span>
            </button>
            
            <button
              onClick={() => setActiveTab('projects')}
              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all relative ${
                activeTab === 'projects'
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {activeTab === 'projects' && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-r-full" />
              )}
              <ClipboardDocumentListIcon className="h-4 w-4" />
              <span>{t('customerPortal.tabs.projects')}</span>
            </button>
            
            <button
              onClick={() => setActiveTab('submissions')}
              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all relative ${
                activeTab === 'submissions'
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {activeTab === 'submissions' && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-r-full" />
              )}
              <DocumentTextIcon className="h-4 w-4" />
              <span>{t('customerPortal.tabs.submissions')}</span>
            </button>
            
            <button
              onClick={() => setActiveTab('missing-items')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-all relative ${
                activeTab === 'missing-items'
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {activeTab === 'missing-items' && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-r-full" />
              )}
              <div className="flex items-center space-x-2">
                <ExclamationTriangleIcon className="h-4 w-4" />
                <span>{t('customerPortal.tabs.missingItems')}</span>
              </div>
              {missingItemsCount > 0 && (
                <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                  {missingItemsCount}
                </span>
              )}
            </button>
            
            <button
              onClick={() => setActiveTab('documents')}
              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all relative ${
                activeTab === 'documents'
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {activeTab === 'documents' && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-r-full" />
              )}
              <FolderIcon className="h-4 w-4" />
              <span>{t('customerPortal.tabs.documents')}</span>
            </button>
            
            <button
              onClick={() => setActiveTab('subscriptions')}
              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all relative ${
                activeTab === 'subscriptions'
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {activeTab === 'subscriptions' && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-r-full" />
              )}
              <CreditCardIcon className="h-4 w-4" />
              <span>Előfizetések</span>
            </button>
          </nav>
        </aside>

        {/* Mobile Sidebar Overlay */}
        {isSidebarOpen && (
          <div className="xl:hidden fixed inset-0 z-40 flex">
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setIsSidebarOpen(false)} />
            <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white dark:bg-gray-800">
              <div className="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                >
                  <XMarkIcon className="h-6 w-6 text-white" />
                </button>
              </div>
              
              {/* Mobile Sidebar Header */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-3">
                  <UserCircleIcon className="h-8 w-8 text-primary-600" />
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {t('customerPortal.title')}
                    </h2>
                  </div>
                </div>
              </div>
              
              <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
                {/* Same buttons as desktop sidebar */}
                <button
                  onClick={() => { setActiveTab('overview'); setIsSidebarOpen(false); }}
                  className={`w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all relative ${
                    activeTab === 'overview'
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {activeTab === 'overview' && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-r-full" />
                  )}
                  <ClipboardDocumentListIcon className="h-4 w-4" />
                  <span>{t('customerPortal.tabs.overview')}</span>
                </button>
                
                <button
                  onClick={() => { setActiveTab('projects'); setIsSidebarOpen(false); }}
                  className={`w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all relative ${
                    activeTab === 'projects'
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {activeTab === 'projects' && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-r-full" />
                  )}
                  <ClipboardDocumentListIcon className="h-4 w-4" />
                  <span>{t('customerPortal.tabs.projects')}</span>
                </button>
                
                <button
                  onClick={() => { setActiveTab('submissions'); setIsSidebarOpen(false); }}
                  className={`w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all relative ${
                    activeTab === 'submissions'
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {activeTab === 'submissions' && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-r-full" />
                  )}
                  <DocumentTextIcon className="h-4 w-4" />
                  <span>{t('customerPortal.tabs.submissions')}</span>
                </button>
                
                <button
                  onClick={() => { setActiveTab('missing-items'); setIsSidebarOpen(false); }}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-all relative ${
                    activeTab === 'missing-items'
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {activeTab === 'missing-items' && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-r-full" />
                  )}
                  <div className="flex items-center space-x-2">
                    <ExclamationTriangleIcon className="h-4 w-4" />
                    <span>{t('customerPortal.tabs.missingItems')}</span>
                  </div>
                  {missingItemsCount > 0 && (
                    <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                      {missingItemsCount}
                    </span>
                  )}
                </button>
                
                <button
                  onClick={() => { setActiveTab('documents'); setIsSidebarOpen(false); }}
                  className={`w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all relative ${
                    activeTab === 'documents'
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {activeTab === 'documents' && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-r-full" />
                  )}
                  <FolderIcon className="h-4 w-4" />
                  <span>{t('customerPortal.tabs.documents')}</span>
                </button>
                
                <button
                  onClick={() => { setActiveTab('subscriptions'); setIsSidebarOpen(false); }}
                  className={`w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all relative ${
                    activeTab === 'subscriptions'
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {activeTab === 'subscriptions' && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-r-full" />
                  )}
                  <CreditCardIcon className="h-4 w-4" />
                  <span>Előfizetések</span>
                </button>
              </nav>
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto relative">
          {/* Mobile Sidebar Indicator - Színes csík bal oldalon */}
          <div className={`
            xl:hidden fixed left-0 top-1/2 -translate-y-1/2 z-20
            w-3 h-24 bg-gradient-to-b from-primary-500 to-blue-500 rounded-r-lg shadow-lg
            transition-all duration-300 cursor-pointer
            ${isSidebarOpen ? 'opacity-0 -translate-x-4' : 'opacity-80 hover:opacity-100 hover:w-4 hover:shadow-xl'}
          `}
            onClick={() => setIsSidebarOpen(true)}
            title="Menü megnyitása"
          />
          
          <div className="p-3 sm:p-4 md:p-6">
            <div className="space-y-4 sm:space-y-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Aktív Kérések - Kék gradiens */}
            <div className="relative overflow-hidden bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group">
              <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full blur-2xl"></div>
              <div className="relative p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-white/20 backdrop-blur-sm rounded-lg">
                    <ClipboardDocumentListIcon className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-white">
                      {tickets.filter(p => p.status !== 'completed' && p.status !== 'cancelled').length}
                    </p>
                  </div>
                </div>
                <p className="text-sm font-medium text-white/90">{t('customerPortal.overview.activeProjects')}</p>
                <div className="mt-2 h-1 bg-white/20 rounded-full overflow-hidden">
                  <div className="h-full bg-white/40 rounded-full w-3/4"></div>
                </div>
              </div>
            </div>

            {/* Beküldött Kérések - Zöld gradiens */}
            <div className="relative overflow-hidden bg-gradient-to-br from-green-500 to-green-600 dark:from-green-600 dark:to-green-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group">
              <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full blur-2xl"></div>
              <div className="relative p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-white/20 backdrop-blur-sm rounded-lg">
                    <DocumentTextIcon className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-white">
                      {tickets.filter(p => p.status === 'submitted').length}
                    </p>
                  </div>
                </div>
                <p className="text-sm font-medium text-white/90">{t('customerPortal.overview.submittedRequests')}</p>
                <div className="mt-2 h-1 bg-white/20 rounded-full overflow-hidden">
                  <div className="h-full bg-white/40 rounded-full w-1/2"></div>
                </div>
              </div>
            </div>

            {/* Hiányzó Dokumentumok - Piros gradiens */}
            <div className="relative overflow-hidden bg-gradient-to-br from-red-500 to-red-600 dark:from-red-600 dark:to-red-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group">
              <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full blur-2xl"></div>
              <div className="relative p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-white/20 backdrop-blur-sm rounded-lg">
                    <ExclamationTriangleIcon className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-white">{missingItemsCount}</p>
                  </div>
                </div>
                <p className="text-sm font-medium text-white/90">{t('customerPortal.overview.missingDocuments')}</p>
                <div className="mt-2 h-1 bg-white/20 rounded-full overflow-hidden">
                  <div className="h-full bg-white/40 rounded-full w-full"></div>
                </div>
              </div>
            </div>

            {/* Lezárt Kérések - Szürke gradiens */}
            <div className="relative overflow-hidden bg-gradient-to-br from-gray-500 to-gray-600 dark:from-gray-600 dark:to-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group">
              <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full blur-2xl"></div>
              <div className="relative p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-white/20 backdrop-blur-sm rounded-lg">
                    <CheckCircleIcon className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-white">
                      {tickets.filter(p => p.status === 'completed').length}
                    </p>
                  </div>
                </div>
                <p className="text-sm font-medium text-white/90">{t('customerPortal.overview.closedProjects')}</p>
                <div className="mt-2 h-1 bg-white/20 rounded-full overflow-hidden">
                  <div className="h-full bg-white/40 rounded-full w-full"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Legutóbbi aktivitás és gyors linkek */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
            {/* Legutóbbi kérések */}
            <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <ClockIcon className="h-5 w-5 text-primary-600" />
                  Legutóbbi kérések
                </h3>
                <button
                  onClick={() => setShowCreateProjectModal(true)}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 text-sm font-medium shadow-sm"
                >
                  <PlusIcon className="h-4 w-4" />
                  Új ticket
                </button>
              </div>
              <div className="p-6">
                {tickets.length === 0 ? (
                  <div className="text-center py-8">
                    <ClipboardDocumentListIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Még nincs kérésed</p>
                    <button
                      onClick={() => setActiveTab('projects')}
                      className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      Új kérés létrehozása
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {tickets.slice(0, 5).map((ticket) => (
                      <div
                        key={ticket.id}
                        onClick={() => {
                          setSelectedProjectDetail(ticket);
                          setShowProjectDetailsModal(true);
                        }}
                        className="flex items-center justify-between p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 dark:text-gray-100 truncate">{ticket.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`px-2 py-0.5 text-xs rounded-full ${
                              ticket.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                              ticket.status === 'in_progress' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                              ticket.status === 'waiting_customer' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' :
                              'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                              {TICKET_STATUSES[ticket.status]}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {ticket.createdAt.toLocaleDateString('hu-HU')}
                            </span>
                          </div>
                        </div>
                        <ChevronRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0 ml-4" />
                      </div>
                    ))}
                    {tickets.length > 5 && (
                      <button
                        onClick={() => setActiveTab('projects')}
                        className="w-full py-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                      >
                        Összes megtekintése ({tickets.length})
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Gyors linkek */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Gyors műveletek</h3>
              </div>
              <div className="p-6 space-y-3">
                <button
                  onClick={() => setActiveTab('projects')}
                  className="w-full flex items-center gap-3 p-4 rounded-lg bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border border-primary-200 dark:border-primary-700 hover:shadow-md transition-all group"
                >
                  <div className="p-2 bg-primary-600 rounded-lg">
                    <PlusIcon className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900 dark:text-gray-100">Új kérés</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Kérdés vagy probléma</p>
                  </div>
                  <ChevronRightIcon className="h-5 w-5 text-gray-400 group-hover:text-primary-600 transition-colors" />
                </button>

                {missingItemsCount > 0 && (
                  <button
                    onClick={() => setActiveTab('missing-items')}
                    className="w-full flex items-center gap-3 p-4 rounded-lg bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 border border-red-200 dark:border-red-700 hover:shadow-md transition-all group"
                  >
                    <div className="p-2 bg-red-600 rounded-lg">
                      <ExclamationTriangleIcon className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-medium text-gray-900 dark:text-gray-100">Hiányzó dokumentumok</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{missingItemsCount} db hiányzik</p>
                    </div>
                    <ChevronRightIcon className="h-5 w-5 text-gray-400 group-hover:text-red-600 transition-colors" />
                  </button>
                )}

                <button
                  onClick={() => setActiveTab('submissions')}
                  className="w-full flex items-center gap-3 p-4 rounded-lg bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border border-blue-200 dark:border-blue-700 hover:shadow-md transition-all group"
                >
                  <div className="p-2 bg-blue-600 rounded-lg">
                    <DocumentTextIcon className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900 dark:text-gray-100">Űrlapok</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Kitöltendő űrlapok</p>
                  </div>
                  <ChevronRightIcon className="h-5 w-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
                </button>

                <button
                  onClick={() => setActiveTab('documents')}
                  className="w-full flex items-center gap-3 p-4 rounded-lg bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border border-green-200 dark:border-green-700 hover:shadow-md transition-all group"
                >
                  <div className="p-2 bg-green-600 rounded-lg">
                    <FolderIcon className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900 dark:text-gray-100">Dokumentumok</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Feltöltött fájlok</p>
                  </div>
                  <ChevronRightIcon className="h-5 w-5 text-gray-400 group-hover:text-green-600 transition-colors" />
                </button>
              </div>
            </div>
          </div>
          </>
        )}

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div className="space-y-4">
            {/* Új kérés gomb */}
            <div className="flex justify-end">
              <button
                onClick={() => setShowCreateProjectModal(true)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center space-x-2 transition-colors shadow-sm"
              >
                <PlusIcon className="h-5 w-5" />
                <span>Új kérés</span>
              </button>
            </div>

            {/* Szűrők */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Keresés */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Keresés
                  </label>
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Keresés címben vagy leírásban..."
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                    />
                  </div>
                </div>

                {/* Státusz szűrő */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Státusz
                  </label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="all" className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">Összes státusz</option>
                    {Object.entries(TICKET_STATUSES).map(([key, label]) => (
                      <option key={key} value={key} className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">{label}</option>
                    ))}
                  </select>
                </div>

                {/* Típus szűrő */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Típus
                  </label>
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="all" className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">Összes típus</option>
                    {Object.entries(TICKET_TYPES).map(([key, label]) => (
                      <option key={key} value={key} className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Találatok száma */}
              {searchTerm || filterStatus !== 'all' || filterType !== 'all' ? (
                <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                  {filteredTickets.length} találat
                </div>
              ) : null}
            </div>

            {projectsLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
                <div className="text-center py-8 sm:py-12">
                  <ClipboardDocumentListIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                    {searchTerm || filterStatus !== 'all' || filterType !== 'all' 
                      ? 'Nincs találat' 
                      : 'Nincs aktív projekt'}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {searchTerm || filterStatus !== 'all' || filterType !== 'all'
                      ? 'Próbálj meg más szűrőket használni'
                      : 'Jelenleg nincsenek aktív projektjei. Hozzon létre egy új kérést!'}
                  </p>
                  {!(searchTerm || filterStatus !== 'all' || filterType !== 'all') && (
                    <button
                      onClick={() => setShowCreateProjectModal(true)}
                      className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 inline-flex items-center space-x-2"
                    >
                      <PlusIcon className="h-5 w-5" />
                      <span>Új kérés létrehozása</span>
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredTickets.map((project) => (
                  <div
                    key={project.id}
                    onClick={() => {
                      setSelectedProjectDetail(project);
                      setShowProjectDetailsModal(true);
                    }}
                    className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm hover:shadow-md transition-all cursor-pointer"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                            {project.title}
                          </h3>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            project.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                            project.status === 'in_progress' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                            project.status === 'waiting_customer' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' :
                            'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                          }`}>
                            {TICKET_STATUSES[project.status]}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          {project.description}
                        </p>
                        {project.customerNotes && (
                          <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-sm text-blue-900 dark:text-blue-200">
                            💬 {project.customerNotes}
                          </div>
                        )}
                        <div className="flex items-center gap-4 mt-3 text-xs text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <ClockIcon className="h-3 w-3" />
                            {project.createdAt.toLocaleDateString('hu-HU')}
                          </span>
                          {project.dueDate && (
                            <span className="text-red-600 dark:text-red-400">
                              Határidő: {project.dueDate.toLocaleDateString('hu-HU')}
                            </span>
                          )}
                          {project.responses && project.responses.length > 0 && (
                            <span className="flex items-center gap-1">
                              💬 {project.responses.length} válasz
                            </span>
                          )}
                          {project.attachments && project.attachments.length > 0 && (
                            <span className="flex items-center gap-1">
                              📎 {project.attachments.length} melléklet
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Gombok */}
                      <div className="ml-4 flex flex-col gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedProjectDetail(project);
                            setShowProjectDetailsModal(true);
                          }}
                          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                        >
                          Részletek
                        </button>
                        {/* Törlés gomb csak akkor jelenik meg, ha az ügyfél hozta létre */}
                        {project.createdBy === user?.id && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTicketDeleteClick(project);
                            }}
                            className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 hover:text-red-700 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/30 dark:hover:bg-red-500/20 dark:hover:border-red-500/50 transition-colors"
                          >
                            🗑️ Törlés
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Responses */}
                    {project.responses && project.responses.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Válaszok:</h4>
                        <div className="space-y-2">
                          {project.responses.filter(r => !r.isInternal).map((response) => (
                            <div
                              key={response.id}
                              className={`p-3 rounded-lg text-sm ${
                                response.isStaff
                                  ? 'bg-gray-50 dark:bg-gray-700/50 ml-0'
                                  : 'bg-primary-50 dark:bg-primary-900/20 mr-0'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-xs text-gray-600 dark:text-gray-400">
                                  {response.createdByName}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-500">
                                  {response.createdAt.toLocaleDateString('hu-HU')}
                                </span>
                              </div>
                              <p className="text-gray-700 dark:text-gray-300">{response.message}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Submissions Tab */}
        {activeTab === 'submissions' && (
          <div className="space-y-4 sm:space-y-6">
            {/* Available Forms Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100 mb-3 sm:mb-4">
                {t('customerPortal.submissions.availableForms')}
              </h3>
              <AvailableForms />
            </div>

            {/* My Submissions Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100 mb-3 sm:mb-4">
                {t('customerPortal.submissions.mySubmissions')}
              </h3>
              <MySubmissions />
            </div>
          </div>
        )}

        {/* Missing Items Tab */}
        {activeTab === 'missing-items' && (
          <div className="space-y-3 sm:space-y-4">
            {/* Warning Messages - dinamikus üzenetek minden hiányzó tételhez */}
            {/* Missing Items List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4 sm:p-6">
              <MissingItemsList customerId={user?.customerId} isCustomerView={true} onUpdate={loadMissingItems} />
            </div>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
            <CustomerDocuments />
          </div>
        )}

        {/* Subscriptions Tab */}
        {activeTab === 'subscriptions' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
            <Subscriptions />
          </div>
        )}
            </div>
          </div>
        </main>
      </div>

      {/* Új kérés létrehozása Modal */}
      <Modal
        isOpen={showCreateProjectModal}
        onClose={() => {
          setShowCreateProjectModal(false);
          setNewProjectForm({
            type: 'question',
            title: '',
            description: '',
            priority: 'medium'
          });
        }}
        title="Új kérés létrehozása"
        size="4xl"
      >
        <form onSubmit={handleCreateProject} className="p-4 space-y-3">
          {/* Alapadatok Section */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                <ClipboardDocumentListIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="p-4 flex-1 space-y-4">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  Kérés adatai
                </h3>

                {/* Type & Priority */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Kérés típusa
                    </label>
                    <select
                      value={newProjectForm.type}
                      onChange={(e) => setNewProjectForm({ ...newProjectForm, type: e.target.value as any })}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {Object.entries(TICKET_TYPES).map(([key, label]) => (
                        <option key={key} value={key} className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Prioritás
                    </label>
                    <select
                      value={newProjectForm.priority}
                      onChange={(e) => setNewProjectForm({ ...newProjectForm, priority: e.target.value as any })}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {Object.entries(TICKET_PRIORITIES).map(([key, label]) => (
                        <option key={key} value={key} className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">{label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tárgy <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={newProjectForm.title}
                    onChange={(e) => setNewProjectForm({ ...newProjectForm, title: e.target.value })}
                    placeholder="pl. Dokumentum kérés"
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Részletek Section */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/20 dark:to-pink-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                <DocumentTextIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="p-4 flex-1 space-y-4">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  Részletes leírás
                </h3>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Írja le részletesen a kérését
                  </label>
                  <textarea
                    value={newProjectForm.description}
                    onChange={(e) => setNewProjectForm({ ...newProjectForm, description: e.target.value })}
                    rows={5}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm resize-none text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                    placeholder="Írja le részletesen, hogy miben segíthetünk..."
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Minél részletesebb a leírás, annál gyorsabban tudunk segíteni.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Mellékletek Section */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-green-100 to-teal-100 dark:from-green-900/20 dark:to-teal-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                <DocumentTextIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div className="p-4 flex-1">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  Mellékletek
                </h3>
                <AttachmentUploader
                  ticketId="new"
                  ticketTitle={newProjectForm.title || 'Új kérés'}
                  ticketType={newProjectForm.type}
                  customerId={user?.customerId || ''}
                  customerName={user?.displayName || ''}
                  organizationId={user?.organizationId || ''}
                  uploadedBy={user?.id || ''}
                  uploadedByName={user?.displayName || ''}
                  uploadedByType="customer"
                  onAttachmentAdded={handleAttachmentAdded}
                />
                {newTicketAttachments.length > 0 && (
                  <div className="mt-4">
                    <AttachmentList
                      attachments={newTicketAttachments}
                      canDelete={false}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowCreateProjectModal(false);
                setNewProjectForm({
                  type: 'question',
                  title: '',
                  description: '',
                  priority: 'medium'
                });
                setNewTicketAttachments([]);
              }}
            >
              Mégse
            </Button>
            <Button type="submit" variant="primary">
              <PlusIcon className="h-4 w-4 mr-2" />
              Kérés elküldése
            </Button>
          </div>
        </form>
      </Modal>

      {/* Projekt részletek Modal */}
      <Modal
        isOpen={showProjectDetailsModal}
        onClose={() => {
          setShowProjectDetailsModal(false);
          setSelectedProjectDetail(null);
          setReplyMessage('');
        }}
        title={selectedProjectDetail?.title || 'Projekt részletek'}
        size="6xl"
      >
        {selectedProjectDetail && (
          <div className="p-4 space-y-4">
            {/* Projekt információk */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center gap-3 mb-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  selectedProjectDetail.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                  selectedProjectDetail.status === 'in_progress' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                  selectedProjectDetail.status === 'waiting_customer' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' :
                  'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                }`}>
                  {TICKET_STATUSES[selectedProjectDetail.status]}
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {TICKET_TYPES[selectedProjectDetail.type]}
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400">•</span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedProjectDetail.createdAt.toLocaleDateString('hu-HU')}
                </span>
              </div>
              
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                {selectedProjectDetail.description}
              </p>
              
              {selectedProjectDetail.customerNotes && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-1">
                    Üzenet a munkatársaktól:
                  </p>
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    {selectedProjectDetail.customerNotes}
                  </p>
                </div>
              )}
            </div>

            {/* Mellékletek */}
            {selectedProjectDetail.attachments && selectedProjectDetail.attachments.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <AttachmentList
                  attachments={selectedProjectDetail.attachments}
                  onDelete={(attachment) => handleAttachmentDeleteClick(attachment, selectedProjectDetail.id)}
                  canDelete={true}
                />
              </div>
            )}

            {/* Válaszok */}
            {selectedProjectDetail.responses && selectedProjectDetail.responses.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Beszélgetés</h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {selectedProjectDetail.responses.filter(r => !r.isInternal).map((response) => (
                    <div
                      key={response.id}
                      className={`p-3 rounded-lg ${
                        response.isStaff
                          ? 'bg-gray-100 dark:bg-gray-700 mr-12'
                          : 'bg-primary-50 dark:bg-primary-900/20 ml-12'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                          {response.createdByName}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {response.createdAt.toLocaleDateString('hu-HU')} {response.createdAt.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-sm text-gray-800 dark:text-gray-200">
                        {response.message}
                      </p>
                      {response.attachments && response.attachments.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                          <AttachmentList
                            attachments={response.attachments}
                            canDelete={false}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Válasz írása */}
            {selectedProjectDetail.status !== 'completed' && selectedProjectDetail.status !== 'cancelled' && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Válasz írása</h3>
                <form onSubmit={handleSendReply} className="space-y-3">
                  <textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    rows={4}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm resize-none text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                    placeholder="Írja ide a válaszát..."
                    required
                  />
                  
                  {/* Mellékletek */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                    <AttachmentUploader
                      ticketId={selectedProjectDetail.id}
                      ticketTitle={selectedProjectDetail.title}
                      ticketType={selectedProjectDetail.type}
                      customerId={selectedProjectDetail.customerId}
                      customerName={selectedProjectDetail.customerName}
                      organizationId={user?.organizationId || ''}
                      uploadedBy={user?.id || ''}
                      uploadedByName={user?.displayName || ''}
                      uploadedByType="customer"
                      onAttachmentAdded={handleReplyAttachmentAdded}
                      maxFiles={3}
                    />
                    {replyAttachments.length > 0 && (
                      <div className="mt-3">
                        <AttachmentList
                          attachments={replyAttachments}
                          canDelete={false}
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end">
                    <Button type="submit" variant="primary">
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Válasz küldése
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {selectedProjectDetail.status === 'completed' && (
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 text-center">
                <CheckCircleIcon className="h-8 w-8 text-green-600 dark:text-green-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-green-800 dark:text-green-200">
                  Ez a kérés lezárásra került
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Delete Attachment Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteAttachmentModal}
        onClose={() => {
          setShowDeleteAttachmentModal(false);
          setDeletingAttachment(null);
        }}
        onConfirm={handleConfirmAttachmentDelete}
        title="Melléklet törlése"
        deleteMessage={`Biztosan törölni szeretnéd a "${deletingAttachment?.attachment.name}" fájlt?`}
        deleteWarning="Ez a művelet nem vonható vissza. A fájl véglegesen törlődik."
      />

      {/* Delete Ticket Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteTicketModal}
        onClose={() => {
          setShowDeleteTicketModal(false);
          setDeletingTicket(null);
        }}
        onConfirm={handleConfirmTicketDelete}
        title="Kérés törlése"
        deleteMessage={`Biztosan törölni szeretnéd a "${deletingTicket?.title}" kérést?`}
        deleteWarning="Ez a művelet nem vonható vissza. A kérés és az összes hozzá tartozó válasz véglegesen törlődik."
      />
    </div>
  );
};

export default CustomerPortal;
